import {
  Avatar,
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';

import { Company } from '@/services/hsm-backend';

export interface CompanyHeaderBannerProps {
  company: Company;
  onRefresh: () => void;
  onChangeCompany: () => void;
}

/**
 * Sticky banner that sits under the top bar on the company detail page.
 * Shows the company avatar + name + monospace companyId + subscription
 * status chip, and exposes two actions:
 * - A refresh icon button → triggers `onRefresh` (typically a refetch).
 * - "Cambiar empresa" → triggers `onChangeCompany` (typically navigates
 *   back to `/`).
 *
 * @param company         Loaded company; drives the rendered identity.
 * @param onRefresh       Handler invoked when the user clicks "Refrescar".
 * @param onChangeCompany Handler invoked when the user clicks "Cambiar empresa".
 */
export const CompanyHeaderBanner = ({
  company,
  onRefresh,
  onChangeCompany,
}: CompanyHeaderBannerProps) => {
  const hasSubscription = Boolean(company.subscription);
  const isActive = company.subscription?.status === 'active';
  const statusColor = !hasSubscription
    ? 'warning'
    : isActive
      ? 'success'
      : 'default';
  const statusDot = !hasSubscription
    ? 'warning.main'
    : isActive
      ? 'success.main'
      : 'text.disabled';
  const statusLabel = !hasSubscription
    ? 'Sin suscripción HSM'
    : isActive
      ? 'Suscripción activa'
      : 'Suscripción inactiva';

  return (
    <Box
      sx={{
        position: 'sticky',
        top: 64,
        zIndex: 1,
        bgcolor: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'divider',
        px: 3,
        py: 1.5,
      }}
    >
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        sx={{ maxWidth: 1080, mx: 'auto' }}
      >
        <Avatar
          sx={{
            bgcolor: 'primary.light',
            color: 'primary.dark',
            fontWeight: 700,
          }}
        >
          {(company.name || '?').charAt(0).toUpperCase()}
        </Avatar>
        <Box sx={{ flexGrow: 1 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Typography variant="h6" component="h2">
              {company.name}
            </Typography>
            <Chip
              size="small"
              color={statusColor}
              icon={
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: statusDot,
                    ml: '6px !important',
                  }}
                />
              }
              label={statusLabel}
            />
          </Stack>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontFamily: 'ui-monospace, monospace' }}
          >
            {company.companyId}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Refrescar">
            <IconButton size="small" onClick={onRefresh}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            size="small"
            startIcon={<SwapHorizIcon />}
            onClick={onChangeCompany}
          >
            Cambiar empresa
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};
